import { Alert, Box, Button, Stack, Divider, Typography } from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import { Refresh } from "@material-ui/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "hooks/useAuth";
import useSettings from "hooks/useSettings";
import useWebotServer from "hooks/useWebotServer";
import { fToNow } from "utils/formatTime";
import { BuyersMatchedToListing } from "components/_dashboard/listingsMatch/BuyersMatchedToListing";
import ListingMatchesTable from "components/_dashboard/listingMatchToContacts/table/ListingMatchesTable";
import {
  isLiveInFlight,
  loadLiveMatches,
  loadSnapshotMatches,
  matchesSessionKey,
  readMatchesSession,
  subscribeMatchesSession
} from "components/_dashboard/listingMatchToContacts/table/matchesSessionCache";
import {
  audienceKey,
  matchAudienceForListing
} from "components/_dashboard/listingMatchToContacts/matchAudience";
import type { MatchesSessionEntry } from "components/_dashboard/listingMatchToContacts/table/matchesSessionCache";
import type { AlgoliaHitResult } from "components/_dashboard/listingsMatch/algoliaClient";

/**
 * A LIVE answer younger than this is shown WITHOUT a refetch — reopening a listing a moment after
 * closing it (or switching dialog tabs) must not start another compute. Older than this it is still
 * shown at once, and refreshed in the background. The refresh button ignores it, and a compute
 * already in flight is joined whatever the entry's age.
 */
const SESSION_FRESH_MS = 60_000;

type MatchedBuyersTabProps = {
  listing: AlgoliaHitResult;
  listingId: string;
};

type StoreView = { key: string; entry: MatchesSessionEntry | undefined; liveLoading: boolean };

function readStoreView(key: string): StoreView {
  return { key, entry: readMatchesSession(key), liveLoading: isLiveInFlight(key) };
}

/**
 * The listing's matched buyers. Reads ONLY the session store (`matchesSessionCache.ts`) — never the
 * Firestore `listing-matches` doc, whose rows carry no reasons, no office groups, and mostly come
 * from the notification flow's different population (that is what showed "no details").
 *
 * On open: a fresh live answer is shown as is; otherwise a live compute starts (deduped app-wide,
 * so a reopen mid-compute JOINS it), and when nothing at all is known the server's last snapshot is
 * read in parallel so reasons and office rows appear in one read instead of after ~25s.
 */
export function MatchedBuyersTab({ listing, listingId }: MatchedBuyersTabProps): JSX.Element {
  const {
    user: { email }
  } = useAuth();
  const { t: translate } = useTranslation();
  const { themeDirection } = useSettings();
  const { getBuyersSuggestionsForListingAndRealtor, getLastBuyersSuggestionsForListing } = useWebotServer();

  const sessionKey = matchesSessionKey(email, listingId);
  // Tagged with its key: if this instance is handed another listing, the render before the effect
  // re-syncs reads the store for the NEW key instead of flashing the previous listing's rows.
  const [view, setView] = useState<StoreView>(() => readStoreView(sessionKey));
  const { entry, liveLoading } = view.key === sessionKey ? view : readStoreView(sessionKey);
  /** The last live load that failed, per key; a live answer received after it clears it. */
  const [liveFailure, setLiveFailure] = useState<{ key: string; at: number } | null>(null);

  const isRTL = themeDirection === "rtl";
  const audience = matchAudienceForListing(listing);

  // The context's functions are new on every render; the loads below must not re-run because of it.
  const fetchersRef = useRef({ getBuyersSuggestionsForListingAndRealtor, getLastBuyersSuggestionsForListing });
  fetchersRef.current = { getBuyersSuggestionsForListingAndRealtor, getLastBuyersSuggestionsForListing };

  // The key this mounted tab is showing; `null` once unmounted. A settled load only touches state
  // when it still belongs to what is on screen — never after unmount, never for a previous listing.
  const activeKeyRef = useRef<string | null>(sessionKey);

  useEffect(() => {
    activeKeyRef.current = sessionKey;
    const sync = (): void => setView(readStoreView(sessionKey));
    sync();
    const unsubscribe = subscribeMatchesSession(sessionKey, sync);
    return () => {
      unsubscribe();
      activeKeyRef.current = null;
    };
  }, [sessionKey]);

  const startLive = useCallback((key: string, id: string): void => {
    setLiveFailure(null);
    loadLiveMatches(key, () => fetchersRef.current.getBuyersSuggestionsForListingAndRealtor(id)).catch(
      (error: unknown) => {
        console.error("MatchedBuyersTab: live matches failed", error);
        if (activeKeyRef.current === key) setLiveFailure({ key, at: Date.now() });
      }
    );
  }, []);

  useEffect(() => {
    if (!listingId) return;
    const current = readMatchesSession(sessionKey);
    const fresh = current?.source === "live" && Date.now() - current.fetchedAt < SESSION_FRESH_MS;
    // Fresh AND idle: nothing to do. Fresh but a compute is running (a refresh started before a tab
    // switch or reopen, or a batch find-buyers): JOIN it — `loadLiveMatches` dedupes, so this starts
    // nothing and only attaches this instance's failure handler, or a failed refresh stays silent.
    if (fresh && !isLiveInFlight(sessionKey)) return;
    startLive(sessionKey, listingId);
    if (!current) {
      loadSnapshotMatches(sessionKey, () => fetchersRef.current.getLastBuyersSuggestionsForListing(listingId)).catch(
        // Best effort: the live compute is already running and is the answer that counts.
        (error: unknown) => console.warn("MatchedBuyersTab: last matches snapshot unavailable", error)
      );
    }
  }, [sessionKey, listingId, startLive]);

  // Forced: bypasses the freshness window, still joins a compute already in flight.
  const handleRefresh = useCallback((): void => {
    if (listingId) startLive(sessionKey, listingId);
  }, [sessionKey, listingId, startLive]);

  // Shown until a NEWER live answer exists (whoever fetched it) or a retry is in flight. Stale rows
  // and snapshot rows stay visible under it.
  const liveFailed =
    liveFailure?.key === sessionKey &&
    !liveLoading &&
    !(entry?.source === "live" && entry.fetchedAt > liveFailure.at);

  const failureAlert = liveFailed ? (
    <Alert
      severity="warning"
      action={
        <Button color="inherit" size="small" onClick={handleRefresh} sx={{ whiteSpace: "nowrap" }}>
          {translate("listingMatchToContactsPage.matchHistory.retry")}
        </Button>
      }
    >
      {translate("listingMatchToContactsPage.matchHistory.refreshFailed")}
    </Alert>
  ) : null;

  return (
    <Stack gap={4}>
      <BuyersMatchedToListing listing={listing} customListingId={listingId} />
      <Divider />
      <Stack gap={2}>
        <Typography variant="h4">{translate(audienceKey(audience, "contactsRecommendation"))}</Typography>
        <MatchHistoryHeader
          computedAt={entry?.computedAt}
          loading={liveLoading}
          onRefresh={handleRefresh}
          isRTL={isRTL}
          translate={translate}
        />
        {failureAlert}
        {/* Nothing to show and the compute failed: the alert IS the state — no skeleton under it. */}
        {!(liveFailed && !entry) && (
          <ListingMatchesTable
            listing={listing}
            listingId={listingId}
            audience={audience}
            // `undefined` while nothing is known — "not loaded", never "none".
            myContacts={entry?.data.fromMyContacts}
            orgGroups={entry?.data.fromOrgContacts}
            orgAccess={entry?.data.orgAccess}
            refreshing={liveLoading && Boolean(entry)}
            provisional={entry?.source === "snapshot"}
            truncated={entry?.truncated}
          />
        )}
      </Stack>
    </Stack>
  );
}

type MatchHistoryHeaderProps = {
  /** When the shown answer was computed (ms epoch); `NaN`/absent = unknown. */
  computedAt: number | undefined;
  loading: boolean;
  onRefresh: () => void;
  isRTL: boolean;
  translate: (key: string | string[], options?: object) => string;
};

function MatchHistoryHeader({ computedAt, loading, onRefresh, isRTL, translate }: MatchHistoryHeaderProps): JSX.Element {
  const validComputedAt = computedAt !== undefined && Number.isFinite(computedAt) ? new Date(computedAt) : null;

  const computedLabel = validComputedAt
    ? translate("listingMatchToContactsPage.matchHistory.lastComputed", {
        when: fToNow(validComputedAt, isRTL)
      })
    : translate("listingMatchToContactsPage.matchHistory.notYetComputed");

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.neutral"
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography variant="subtitle2" color="text.primary">
          {computedLabel}
        </Typography>
        <LoadingButton size="small" variant="outlined" loading={loading} startIcon={<Refresh />} onClick={onRefresh}>
          {translate("listingMatchToContactsPage.matchHistory.refresh")}
        </LoadingButton>
      </Stack>
    </Box>
  );
}
